import React, { Fragment, useCallback, useContext, useEffect, useMemo } from "react";
import {
    CardStyleInterpolators,
    createStackNavigator,
    StackCardInterpolatedStyle,
    StackCardInterpolationProps,
    StackScreenProps,
    TransitionPreset,
} from "@react-navigation/stack";
import CreateMissionJobTitle from "../screens/create-mission/CreateMissionJobTitle";
import CreateMissionSummary from "../screens/create-mission/CreateMissionSummary";
import CreateMissionManager, {
    CreateMissionScreensContext,
    getInitialMissionScreen,
} from "../contexts/CreateMissionManager";
import CreateMissionCCT from "../screens/create-mission/CreateMissionCCT";
import CreateMissionDuration from "../screens/create-mission/CreateMissionDuration";
import CreateMissionEarningType from "../screens/create-mission/CreateMissionEarningType";
import CreateMissionWorkingAddress from "../screens/create-mission/CreateMissionWorkingAddress";
import CreateMissionSalary from "../screens/create-mission/CreateMissionSalary";
import CreateMissionFees from "../screens/create-mission/CreateMissionFees";
import CreateMissionDeductions from "../screens/create-mission/CreateMissionDeductions";
import CreateMissionTimeSheet from "../screens/create-mission/CreateMissionTimeSheet";
import CreateMissionWorkingTimeMore from "../screens/create-mission/CreateMissionWorkingTimeMore";
import CreateMissionWorker from "../screens/create-mission/CreateMissionWorker";
import CreateMissionPreSign from "../screens/create-mission/CreateMissionPreSign";
import CreateMissionHotellerieRestaurantFees from "../screens/create-mission/CreateMissionHotellerieRestaurantFees";
import { StyleSheet, View } from "react-native";
import { useTheme } from "../hooks/use-theme";
import { RootStackParamList } from "./RootNavigation";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import { useCurrentProfile } from "../hooks/use-current-profile";
import { ClientProfile } from "../__generated__/graphql";
import { useStepsHeader } from "../hooks/use-steps-header";
import DocuIllSvg from "../assets/images/docu_ill.svg";
import StepsManager from "../contexts/StepsManager";
import { STEPS_DURATION } from "../ui_atoms/Steps";
import { useTracking } from "../hooks/tracking";

/****
 * This is where a new step in Mission Creation should be added.
 * After adding the step name here, you should:
 * - Add a property in CreateMissionStackParamList
 * - Associate the React component (screen) with the step name in StackStepInfo
 * - Add the screen in the `screensFlow` in CreateMissionManager. It will define when the screen will be displayed
 * - Implement the component in folder screens/create-mission
 */
export const missionScreens = [
    "CreateMissionWorker",
    "CreateMissionWorkingAddress",
    "CreateMissionJobTitle",
    "CreateMissionCCT",
    "CreateMissionHotellerieRestaurantFees",
    "CreateMissionTimeSheet",
    "CreateMissionEarningType",
    "CreateMissionWorkingTimeMore",
    "CreateMissionDuration",
    "CreateMissionSalary",
    "CreateMissionFees",
    "CreateMissionDeductions",
    "CreateMissionSummary",
    "CreateMissionPreSign",
] as const;

export type MissionScreens = (typeof missionScreens)[number];

export type CreateMissionStackParamList = {
    CreateMissionJobTitle: undefined;
    CreateMissionSummary: undefined;
    CreateMissionWorker: undefined;
    CreateMissionCCT: undefined;
    CreateMissionHotellerieRestaurantFees: undefined;
    CreateMissionDuration: undefined;
    CreateMissionEarningType: undefined;
    CreateMissionWorkingAddress: undefined;
    CreateMissionWorkingTimeMore: undefined;
    CreateMissionSalary: undefined;
    CreateMissionFees: undefined;
    CreateMissionDeductions: undefined;
    CreateMissionTimeSheet: undefined;
    CreateMissionPreSign: { contractId: string };
};

const Stack = createStackNavigator<CreateMissionStackParamList>();

const StackStepInfo = {
    CreateMissionJobTitle: CreateMissionJobTitle,
    CreateMissionWorker: CreateMissionWorker,
    CreateMissionWorkingAddress: CreateMissionWorkingAddress,
    CreateMissionCCT: CreateMissionCCT,
    CreateMissionHotellerieRestaurantFees: CreateMissionHotellerieRestaurantFees,
    CreateMissionDuration: CreateMissionDuration,
    CreateMissionEarningType: CreateMissionEarningType,
    CreateMissionWorkingTimeMore: CreateMissionWorkingTimeMore,
    CreateMissionSalary: CreateMissionSalary,
    CreateMissionFees: CreateMissionFees,
    CreateMissionDeductions: CreateMissionDeductions,
    CreateMissionTimeSheet: CreateMissionTimeSheet,
    CreateMissionSummary: CreateMissionSummary,
    CreateMissionPreSign: CreateMissionPreSign,
};

type CreateMissionStackProps = StackScreenProps<RootStackParamList, "CreateMissionStack">;

const CreateMissionStack: React.FC<CreateMissionStackProps> = ({ route }) => (
    <CreateMissionManager initialParams={{ workerInfo: route.params?.workerInfo }}>
        <StepsManager>
            <CreateMissionStackContent />
        </StepsManager>
    </CreateMissionManager>
);

const CreateMissionStackContent = () => {
    const createMissionStyleInterpolator = useCreateMissionStyleInterpolator();
    const isLarge = useIsScreenSizeMin("large");
    const styles = useStyles();
    const { colors } = useTheme();
    const currentProfile = useCurrentProfile<ClientProfile>();
    const [StackWrapper, wrapperProps] = useMemo(
        () => (isLarge ? [View, { style: styles.stackWrapper }] : [Fragment, {}]),
        [isLarge, styles.stackWrapper],
    );
    const { trackEvent } = useTracking();
    const initialRouteName = getInitialMissionScreen(currentProfile) as MissionScreens;

    useEffect(() => {
        void trackEvent("mission_creation_started");
    }, [trackEvent]);

    const { closeWizard, currentStepIndex, totalSteps, stepsInfo } = useContext(
        CreateMissionScreensContext,
    );

    const { headerComponent, screenListeners } = useStepsHeader<keyof CreateMissionStackParamList>({
        onClose: closeWizard,
        getCurrentIndex: currentStepIndex,
        getTotalSteps: () => totalSteps,
        headerHidden: stepsInfo.filter((e) => e.isVisible).map((e) => e.noHeader || false),
        stepsConfig: {
            seenColor: colors.primary,
            currentColor: colors.accentLighter,
            currentStep: {
                component: CurrentStep,
                size: CURRENT_STEP_SIZE,
            },
        },
    });

    return (
        <StackWrapper {...wrapperProps}>
            {headerComponent}
            <Stack.Navigator
                initialRouteName={initialRouteName}
                screenListeners={screenListeners}
                screenOptions={{
                    headerMode: "float",
                    headerTransparent: false,
                    detachPreviousScreen: false,
                    headerShadowVisible: false,
                    headerBackButtonDisplayMode: "minimal",
                    headerTitleAlign: "center",
                    headerShown: false,
                    headerStatusBarHeight: 0,
                    cardStyle: { overflow: "visible" },
                    cardStyleInterpolator: createMissionStyleInterpolator,
                }}
            >
                {missionScreens.map((c) => (
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <Stack.Screen
                        key={c}
                        name={c}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        component={StackStepInfo[c]}
                        options={{
                            transitionSpec: {
                                open: config,
                                close: config,
                            },
                        }}
                    />
                ))}
            </Stack.Navigator>
        </StackWrapper>
    );
};

const CURRENT_STEP_SIZE = 44;

const CurrentStep = <DocuIllSvg width="100%" height="100%" />;

const useCreateMissionStyleInterpolator = () => {
    const cardStyleInterpolator = useCallback<
        (props: StackCardInterpolationProps) => StackCardInterpolatedStyle
    >(({ ...props }) => {
        const {
            containerStyle = {},
            cardStyle = {},
            overlayStyle = {},
            shadowStyle = {},
        } = CardStyleInterpolators.forFadeFromCenter(props) as {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            containerStyle: Record<string, any>;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            cardStyle: Record<string, any>;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            overlayStyle: Record<string, any>;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            shadowStyle: Record<string, any>;
        };

        return {
            containerStyle: {
                ...containerStyle,
            },
            cardStyle: {
                ...cardStyle,
            },
            overlayStyle: {
                ...overlayStyle,
                opacity: 0,
            },
            shadowStyle: {
                ...shadowStyle,
            },
        };
    }, []);

    return cardStyleInterpolator;
};

const config: TransitionPreset["transitionSpec"]["open"] = {
    animation: "timing",
    config: {
        duration: STEPS_DURATION,
    },
};

const useStyles = () => {
    const { roundness } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                stackWrapper: {
                    width: 700,
                    height: "85%",
                    maxHeight: 900,
                    margin: "auto",
                    borderRadius: roundness * 3,
                    overflow: "hidden",
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    boxShadow: "rgba(17, 12, 46, 0.15) 0px 48px 100px 0px",
                },
            }),
        [roundness],
    );
};

export default CreateMissionStack;
